import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@material-ui/core';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import type { JsonObject } from '@backstage/types';
import {
  configApiRef,
  fetchApiRef,
  type ApiHolder,
} from '@backstage/core-plugin-api';
import {
  deriveAccessRequestBeneficiaryUsername,
  isTruthyFlag,
} from './deriveAccessRequestBeneficiaryUsername';

const ACCESS_LEVELS = [
  'guest',
  'reporter',
  'developer',
  'maintainer',
  'owner',
] as const;

export async function validateGitLabAccessLevel(
  value: string,
  validation: FieldValidation,
  context: { apiHolder: ApiHolder; formData?: JsonObject },
): Promise<void> {
  const requested = (value || '').trim().toLowerCase();
  if (!requested) {
    validation.addError('Access level is required.');
    return;
  }

  const formData = context.formData;
  const selection =
    typeof formData?.project_name === 'string'
      ? formData.project_name.trim()
      : '';
  if (!selection) {
    validation.addError(
      'Select a GitLab project or group before choosing access level.',
    );
    return;
  }

  const section = formData?.gitlab_username_section as
    | {
        raising_for_other?: unknown;
        other_username?: unknown;
        self_username?: unknown;
      }
    | undefined;
  const raisingForOther =
    isTruthyFlag(section?.raising_for_other) ||
    isTruthyFlag(formData?.['gitlab_username_section.raising_for_other']);

  const username = deriveAccessRequestBeneficiaryUsername(formData);

  if (raisingForOther && !username) {
    validation.addError(
      "Enter the other user's GitLab username (Raising for other user).",
    );
    return;
  }
  if (!username) {
    validation.addError(
      'Could not determine the GitLab username for the person receiving access.',
    );
    return;
  }

  const configApi = context.apiHolder.get(configApiRef);
  const fetchApi = context.apiHolder.get(fetchApiRef);
  if (!configApi || !fetchApi) {
    validation.addError('Unable to validate access (APIs unavailable).');
    return;
  }

  const backendBaseUrl = configApi.getString('backend.baseUrl');
  const params = new URLSearchParams({
    selection,
    username,
    requestedAccess: requested,
  });
  const url = `${backendBaseUrl}/api/gitlab-access-request/effective-access?${params}`;

  try {
    const res = await fetchApi.fetch(url);
    const data = (await res.json().catch(() => ({}))) as {
      allowed?: boolean;
      message?: string;
      error?: string;
    };
    if (!res.ok) {
      validation.addError(
        typeof data.error === 'string'
          ? data.error
          : `Access validation failed (HTTP ${res.status}).`,
      );
      return;
    }
    if (data.allowed === false) {
      validation.addError(
        data.message ||
          `Cannot be granted: requested role is less than or equal to ${username}'s current role (including inherited access).`,
      );
    }
  } catch (e) {
    validation.addError(
      e instanceof Error
        ? `Access validation failed: ${e.message}`
        : 'Access validation failed.',
    );
  }
}

/**
 * Access-level select with scaffolder validation that blocks Next when the
 * requested role is ≤ the beneficiary's current (inherited) role on the selection.
 */
export const GitLabAccessLevelField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const error = Boolean(rawErrors?.length);
  const helper =
    rawErrors && rawErrors.length > 0
      ? rawErrors.join(' ')
      : schema.description ||
        "Access requested for the project or group. Must be higher than the recipient's current role (including inherited).";

  return (
    <FormControl margin="normal" required={required} error={error} fullWidth>
      <InputLabel id="gitlab-access-level-label" shrink>
        {schema.title || 'Access level'}
      </InputLabel>
      <Select
        labelId="gitlab-access-level-label"
        value={formData ?? ''}
        displayEmpty
        onChange={e => onChange(String(e.target.value))}
      >
        <MenuItem value="" disabled>
          <em>Select access level</em>
        </MenuItem>
        {ACCESS_LEVELS.map(level => (
          <MenuItem key={level} value={level}>
            {level}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText error={error}>{helper}</FormHelperText>
    </FormControl>
  );
};
